import { useWealthOverview } from '@egw/api'
import { Button, ButtonVariant, ModalDrawer, PageHeaderRedesign, Popover, useModalState } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { RecentActivity } from '@egw/features-transactions'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { CheckingOverflowMenu } from '../../../components/checking-overflow-menu/checking-overflow-menu'

interface CheckingAccountPageProps {
  alias: number
}

export const CheckingAccountRedesignPage: FC<CheckingAccountPageProps> = ({ alias }) => {
  const { isOpen: overflowMenuOpen, openModal: openOverflowMenu, closeModal: closeOverflowMenu } = useModalState()
  const { t } = useTranslation('features-account')
  const navigate = useNavigate()

  const { data: wealthOverviewData, isLoading, error, dataUpdatedAt } = useWealthOverview()

  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  const data = wealthOverviewData?.highYieldChecking?.accounts.find((account) => account.alias === alias)

  const totalBalanceCurrencyCode = data?.balance.total.currencyCode
  const totalBalanceAmount = data?.balance.total.amount
  const availableBalanceCurrencyCode = data?.balance.available.currencyCode
  const availableBalanceAmount = data?.balance.available.amount
  const apy = data?.apy?.toFixed(2) ?? 0

  return (
    <>
      {isLoading ? (
        <div className="p-xs bg-primary-500 typo-head-1 egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {error ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('checking.error')}</p>
        </div>
      ) : null}

      {data ? (
        <>
          <PageHeaderRedesign
            subtitle={t(`bank_account_titles.${data.accountTitle}`).toUpperCase()}
            title={data.accountName}
            // TODO: BACK LINK WILL EVENTUALLY GO BACK TO CASH OVERVIEW
            backLinkUrl={WEALTH_ROUTES.CASH_ACCOUNTS_OVERVIEW.path}
            lastRefreshed={lastRefreshed}
          />
          <div className="egw-container">
            <div className="overview-card mb-md">
              <h3 className="typo-primary-sm-light text-text-header">{t('checking.total_balance')}</h3>
              <p className="typo-primary-2xl-regular text-text-header">
                {totalBalanceCurrencyCode ? <span>{getCurrencySymbol(totalBalanceCurrencyCode)}</span> : null}
                <span>
                  {totalBalanceAmount
                    ? Currency.integerCents.onlyMinus(totalBalanceAmount)
                    : Currency.integerCents.onlyMinus(0)}
                </span>
              </p>
              <div className="flex typo-primary-base-regular text-text-meta mb-2xs">
                <p className="flex-1">{t('checking.available_balance')}</p>
                <div>
                  {availableBalanceCurrencyCode ? <span>{getCurrencySymbol(availableBalanceCurrencyCode)}</span> : null}
                  <span>
                    {availableBalanceAmount
                      ? Currency.integerCents.onlyMinus(availableBalanceAmount)
                      : Currency.integerCents.onlyMinus(0)}
                  </span>
                </div>
              </div>
              {apy ? (
                <div className="flex typo-primary-base-regular text-text-meta flex flex-row justify-between">
                  <div className="flex flex-row gap-1 items-center">
                    <p className="flex-1">{t('checking.apy_label')}</p>
                    <Popover iconClassnames="text-icons-meta" title={t('checking.apy_label')}>
                      {t('checking.apy_description', { apy })}
                    </Popover>
                  </div>
                  <p>{t('checking.apy', { apy })}</p>
                </div>
              ) : null}
            </div>

            <div className="flex gap-4 mb-10">
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  size="small"
                  className="w-full py-4"
                  testId="checking-transfer-button"
                  onClick={() => {
                    sendEvent('user_checkingTransfer_click', {
                      eventSource: 'egWealth',
                      category: 'transfer',
                      subCategory: 'intent',
                    })
                    void navigate(WEALTH_ROUTES.TRANSFER.path)
                  }}
                >
                  {t('account_transfer.transfer')}
                </Button>
              </div>
              <div className="flex-1">
                <Button
                  variant={ButtonVariant.Secondary}
                  size="small"
                  className="w-full py-4"
                  testId="checking-account-options-button"
                  onClick={openOverflowMenu}
                >
                  {t('default.account_options.account_info')}
                </Button>
              </div>
            </div>

            <ModalDrawer
              title={t('default.account_options_label')}
              isOpen={overflowMenuOpen}
              onClose={closeOverflowMenu}
            >
              <CheckingOverflowMenu alias={alias} />
            </ModalDrawer>
          </div>
        </>
      ) : null}

      {data?.alias !== undefined ? <RecentActivity alias={data.alias} /> : null}
    </>
  )
}
